import type { ComponentProps } from 'react'
import { useEffect, useState } from 'react'
import { NumberInput } from './number-input'

export type NumberDraftInputProps = {
  value: number | null | undefined
  onChange: (value: number | undefined) => void
  mode?: 'integer' | 'decimal' | 'signed'
  step?: number
  min?: number
  max?: number
  showSteppers?: boolean
  id?: string
  placeholder?: string
  disabled?: boolean
  className?: string
  'aria-label'?: string
  'aria-invalid'?: ComponentProps<'input'>['aria-invalid']
}

export function NumberDraftInput({
  value,
  onChange,
  mode = 'decimal',
  step,
  min,
  max,
  showSteppers,
  id,
  placeholder,
  disabled,
  className,
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid,
}: NumberDraftInputProps) {
  const [draft, setDraft] = useState<string>(() =>
    value == null ? '' : String(value),
  )
  const [isFocused, setIsFocused] = useState(false)

  useEffect(() => {
    if (isFocused) return
    setDraft(value == null ? '' : String(value))
  }, [value, isFocused])

  const handleChange = (next: string) => {
    setDraft(next)
    if (next === '') {
      onChange(undefined)
      return
    }
    const parsed = mode === 'integer' ? Number.parseInt(next, 10) : Number.parseFloat(next)
    onChange(Number.isNaN(parsed) ? undefined : parsed)
  }

  return (
    <NumberInput
      mode={mode}
      step={step}
      min={min}
      max={max}
      showSteppers={showSteppers}
      value={draft}
      onChange={handleChange}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      id={id}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      aria-label={ariaLabel}
      aria-invalid={ariaInvalid}
    />
  )
}
